import type { SubjectTypeDefinitionSaveFailedAction } from '@Application/SubjectTypeDefinitions/Actions/subjectTypeDefinitionSaveFailed';
import FetchSubjectTypeDefinition from '@Application/SubjectTypeDefinitions/Queries/FetchSubjectTypeDefinition';
import RestoreSubjectType from '@Application/SubjectTypeDefinitions/Commands/RestoreSubjectType';
import fetchSubjectTypeDefinition from '@Application/SubjectTypeDefinitions/fetchSubjectTypeDefinition';
import restoreSubjectType from '@Application/SubjectTypeDefinitions/restoreSubjectType';
import saveSubjectTypeDefinition from '@Application/SubjectTypeDefinitions/saveSubjectTypeDefinition';
import ConditionalLoader from '@Guidelines/Component/Loader/ConditionalLoader';
import ignoreHandledError from '@UI/Helpers/ignoreHandledError';
import ArchiveSubjectTypeDialog from '@UI/Pages/Administration/SubjectTypes/Shared/ConfirmationDialogs/ArchiveSubjectTypeDialog';
import SubjectTypeDefinitionFormData from '@UI/Pages/Administration/SubjectTypes/SubjectTypeEdit/FormData/SubjectTypeDefinitionFormData';
import useStrictAuthenticatedUser from '@UI/Providers/AuthenticatedUserProvider/useStrictAuthenticatedUser';
import SubjectTypeDefinitionView from '@UI/Store/SubjectTypeDefinitions/Views/SubjectTypeDefinitionView';
import { Archive, ArchiveRestore } from 'lucide-react';
import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from '@verclam/synapse';
import { Button } from '@verclam/ui-kit';

type Props = {
	readonly formData: SubjectTypeDefinitionFormData;
	readonly onChange: (
		update: (formData: SubjectTypeDefinitionFormData) => SubjectTypeDefinitionFormData,
	) => void;
	/** Null while the type does not exist yet. */
	readonly definition: SubjectTypeDefinitionView | null;
};

/**
 * Header actions of the editor. An active type can be archived (after confirmation) and saved; a new
 * type can only be saved, and leads to its own editor; an archived type can only be restored. A saved
 * type reseeds the draft from the server's answer (it carries the derived keys). A refused save puts
 * each field error back on its part of the draft; the toast shows the message.
 */
const SubjectTypeEditActions: React.FC<Props> = ({ formData, onChange, definition }) => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const { user } = useStrictAuthenticatedUser();
	const accountId = user.account?.id ?? null;
	const [isSaving, setIsSaving] = React.useState(false);
	const [isRestoring, setIsRestoring] = React.useState(false);
	const [isArchiveVisible, setIsArchiveVisible] = React.useState(false);

	const reloadDefinition = React.useCallback(() => {
		if (accountId === null || definition === null) {
			return;
		}

		dispatch(fetchSubjectTypeDefinition(new FetchSubjectTypeDefinition(accountId, definition.id))).catch(
			ignoreHandledError,
		);
	}, [accountId, definition, dispatch]);

	const handleSaveClick = React.useCallback(() => {
		if (accountId === null) {
			return;
		}

		if (!formData.isFormDataValid()) {
			onChange((current) => current.showErrors());
			return;
		}

		setIsSaving(true);
		dispatch(saveSubjectTypeDefinition(formData.toCommand(accountId, definition?.id ?? null)))
			.then((action) => {
				if (action.type !== 'SUBJECT_TYPE_DEFINITION_SAVED') {
					return;
				}

				if (definition === null) {
					navigate(`/administration/subject-types/${action.payload.id}`, { replace: true });
					return;
				}

				onChange(() =>
					SubjectTypeDefinitionFormData.fromView(new SubjectTypeDefinitionView(action.payload)),
				);
			})
			.catch((failure: SubjectTypeDefinitionSaveFailedAction) => {
				onChange((current) => current.applyServerErrors(failure.payload?.fieldErrors ?? []));
			})
			.finally(() => setIsSaving(false));
	}, [accountId, definition, dispatch, formData, navigate, onChange]);

	const handleArchiveClick = React.useCallback(() => {
		setIsArchiveVisible(true);
	}, []);

	const handleArchiveHide = React.useCallback(() => {
		setIsArchiveVisible(false);
	}, []);

	const handleRestoreClick = React.useCallback(() => {
		if (accountId === null || definition === null) {
			return;
		}

		setIsRestoring(true);
		dispatch(restoreSubjectType(new RestoreSubjectType(accountId, definition.id)))
			.then(reloadDefinition)
			.catch(ignoreHandledError)
			.finally(() => setIsRestoring(false));
	}, [accountId, definition, dispatch, reloadDefinition]);

	if (definition?.isArchived) {
		return (
			<Button
				type="button"
				icon={<ArchiveRestore size={16} />}
				label="Restaurer"
				loading={isRestoring}
				onClick={handleRestoreClick}
			/>
		);
	}

	return (
		<React.Fragment>
			<ConditionalLoader data={definition}>
				{(savedDefinition) => (
					<React.Fragment>
						<Button
							type="button"
							severity="danger"
							outlined
							icon={<Archive size={16} />}
							label="Archiver"
							onClick={handleArchiveClick}
						/>
						<ArchiveSubjectTypeDialog
							subjectType={savedDefinition}
							visible={isArchiveVisible}
							onHide={handleArchiveHide}
							onArchived={reloadDefinition}
						/>
					</React.Fragment>
				)}
			</ConditionalLoader>
			<Button type="button" label="Enregistrer" loading={isSaving} onClick={handleSaveClick} />
		</React.Fragment>
	);
};

export default SubjectTypeEditActions;
